import { useAuth } from "@/components/AuthProvider";
import { useBilling } from "@/components/BillingProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Sparkles, ShieldCheck, CalendarClock, Download, Trash2, AlertTriangle, Languages, Mail as MailIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import i18n from "@/i18n";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function StatusPill({ status }) {
  const map = {
    active:   { l: "Activa", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
    free:     { l: "Gratis", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
    trialing: { l: "Gratis", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
    canceled: { l: "Cancelada", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
    expired:  { l: "Gratis", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
    past_due: { l: "Por pagar", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-300" },
  };
  const v = map[status] || map.free;
  return <span className={cn("text-xs px-2 py-0.5 rounded-full font-semibold", v.cls)}>{v.l}</span>;
}

export default function Settings() {
  const { user, logout, setUser } = useAuth();
  const { billing } = useBilling();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const changeLang = async (lang) => {
    if (lang === i18n.language) return;
    await i18n.changeLanguage(lang);
    document.documentElement.setAttribute("lang", lang);
    try {
      await api.patch("/users/me/language", { language: lang });
      if (setUser && user) setUser({ ...user, language: lang });
      toast.success(t("settings.languageUpdated"));
    } catch {
      /* silent */
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/auth/me/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `sprintroll-export-${today}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const c = res.data?.counts || {};
      toast.success(`Exportación lista: ${c.boards || 0} boards · ${c.cards || 0} tareas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo exportar tus datos");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "ELIMINAR") {
      toast.error("Escribe ELIMINAR para confirmar.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/me", { data: { confirm: "ELIMINAR" } });
      toast.success("Cuenta eliminada. Hasta pronto.");
      await logout().catch(() => {});
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar la cuenta");
      setDeleting(false);
    }
  };

  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString(); } catch { return s; }
  };

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10" data-testid="settings-page">
      <div className="max-w-2xl mx-auto space-y-8 animate-fade-in-up">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Cuenta</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Configuración</h1>
        </div>

        {/* Perfil */}
        <section className="glass-panel rounded-3xl p-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback>{user?.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-display text-xl font-semibold">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </section>

        {/* Suscripción */}
        <section className="glass-panel rounded-3xl p-6" data-testid="billing-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-500" />
              <h2 className="font-display text-lg font-semibold">Suscripción</h2>
              {billing && <StatusPill status={billing.subscription_status} />}
            </div>
            <Button onClick={() => navigate("/pricing")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="settings-upgrade">
              <Sparkles className="h-4 w-4 mr-2" /> {billing?.subscription_status === "active" ? "Cambiar plan" : "Ver planes"}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Plan</p>
              <p className="font-medium mt-1">{billing?.plan ? billing.plan.charAt(0).toUpperCase() + billing.plan.slice(1) : "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Ciclo</p>
              <p className="font-medium mt-1">{billing?.billing_cycle === "monthly" ? "Mensual" : billing?.billing_cycle === "annual" ? "Anual" : "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Moneda</p>
              <p className="font-medium mt-1">{billing?.currency?.toUpperCase() || "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Usuarios</p>
              <p className="font-medium mt-1">{billing?.seats || 1}</p>
            </div>
            <div className="col-span-2 flex items-center gap-2 text-muted-foreground">
              <CalendarClock className="h-4 w-4" />
              {billing?.subscription_status === "active" && billing?.current_period_end ? (
                <span>Renovación: {fmtDate(billing?.current_period_end)}</span>
              ) : billing?.subscription_status === "free" || billing?.subscription_status === "trialing" || billing?.subscription_status === "expired" ? (
                <span>Plan gratuito · sin renovación programada</span>
              ) : billing?.current_period_end ? (
                <span>Renovación: {fmtDate(billing?.current_period_end)}</span>
              ) : (
                <span>Sin renovación programada</span>
              )}
            </div>
          </div>
        </section>

        {/* Idioma / Language */}
        <section className="glass-panel rounded-3xl p-6" data-testid="settings-language-section">
          <div className="flex items-center gap-2 mb-1">
            <Languages className="h-5 w-5 text-blue-500" />
            <h2 className="font-display text-lg font-semibold">{t("settings.language")}</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">{t("settings.languageDesc")}</p>
          <div className="flex gap-2" data-testid="settings-language-options">
            <button
              onClick={() => changeLang("es")}
              data-testid="settings-lang-es"
              className={cn(
                "flex-1 sm:flex-none min-w-[140px] px-4 py-2 rounded-xl border transition text-sm font-medium",
                i18n.language === "es"
                  ? "bg-blue-500/10 border-blue-500/40 text-blue-700 dark:text-blue-300"
                  : "bg-white/40 dark:bg-slate-900/30 border-white/40 dark:border-white/10 hover:bg-white/60"
              )}
            >
              🇲🇽 {t("settings.spanish")}
            </button>
            <button
              onClick={() => changeLang("en")}
              data-testid="settings-lang-en"
              className={cn(
                "flex-1 sm:flex-none min-w-[140px] px-4 py-2 rounded-xl border transition text-sm font-medium",
                i18n.language === "en"
                  ? "bg-blue-500/10 border-blue-500/40 text-blue-700 dark:text-blue-300"
                  : "bg-white/40 dark:bg-slate-900/30 border-white/40 dark:border-white/10 hover:bg-white/60"
              )}
            >
              🇺🇸 {t("settings.english")}
            </button>
          </div>
        </section>

        {/* Notificaciones por email por tablero */}
        <BoardEmailPrefsSection />

        {/* Sesión */}
        <section className="glass-panel rounded-3xl p-6">
          <h2 className="font-display text-lg font-semibold mb-1">{t("settings.session")}</h2>
          <p className="text-sm text-muted-foreground mb-4">{t("settings.sessionDesc")}</p>
          <Button onClick={handleLogout} variant="destructive" data-testid="settings-logout"><LogOut className="h-4 w-4 mr-2" /> {t("nav.signOut")}</Button>
        </section>

        {/* Datos */}
        <section className="glass-panel rounded-3xl p-6" data-testid="settings-data-section">
          <div className="flex items-center gap-2 mb-1">
            <Download className="h-5 w-5 text-blue-500" />
            <h2 className="font-display text-lg font-semibold">Mis datos</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Descarga una copia completa de todos tus workspaces, boards, listas, tarjetas, etiquetas, checklists,
            comentarios e historial de actividad en formato JSON. Es tu información — siempre puedes llevártela.
          </p>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="settings-export-button"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Preparando…" : "Exportar mis datos (JSON)"}
          </Button>
        </section>

        {/* Zona peligrosa */}
        <section
          className="rounded-3xl p-6 border border-rose-300/60 dark:border-rose-500/30 bg-gradient-to-br from-rose-50/70 to-white/30 dark:from-rose-950/30 dark:to-slate-900/30 backdrop-blur-xl shadow-[0_8px_32px_-12px_rgba(244,63,94,0.25)]"
          data-testid="settings-danger-section"
        >
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-5 w-5 text-rose-500" />
            <h2 className="font-display text-lg font-semibold text-rose-700 dark:text-rose-300">Zona peligrosa</h2>
          </div>
          <p className="text-sm text-rose-700/80 dark:text-rose-200/80 mb-4">
            Eliminar tu cuenta borrará <strong>permanentemente</strong> todos tus workspaces, boards, tareas, comentarios
            y suscripción. Esta acción no se puede deshacer. Te recomendamos exportar tus datos antes.
          </p>

          <AlertDialog onOpenChange={(o) => !o && setConfirmText("")}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="settings-delete-account-button">
                <Trash2 className="h-4 w-4 mr-2" /> Eliminar mi cuenta
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="glass-panel border-rose-300/60 dark:border-rose-500/30" data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display flex items-center gap-2 text-rose-700 dark:text-rose-300">
                  <AlertTriangle className="h-5 w-5" /> ¿Eliminar tu cuenta?
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <span className="block">
                    Esta acción es <strong>irreversible</strong>. Vas a perder:
                  </span>
                  <ul className="list-disc pl-5 text-sm space-y-0.5">
                    <li>Todos tus workspaces, boards, listas y tarjetas.</li>
                    <li>Comentarios, checklists e historial.</li>
                    <li>Tu suscripción activa (no se reembolsa el período ya pagado).</li>
                    <li>Tu acceso a boards compartidos por otros.</li>
                  </ul>
                  <span className="block pt-2">
                    Para confirmar, escribe <strong>ELIMINAR</strong> en el campo de abajo:
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Escribe ELIMINAR"
                autoComplete="off"
                className="bg-white/70 dark:bg-slate-900/40 border-rose-300/60 dark:border-rose-500/30"
                data-testid="delete-account-confirm-input"
              />
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-account-cancel">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={deleting || confirmText.trim().toUpperCase() !== "ELIMINAR"}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="delete-account-confirm"
                >
                  {deleting ? "Eliminando…" : "Sí, eliminar todo"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      </div>
    </div>
  );
}

function BoardEmailPrefsSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["my-board-notifs"],
    queryFn: async () => (await api.get("/users/me/board-notifications")).data,
  });
  const toggleMut = useMutation({
    mutationFn: async ({ boardId, subscribe }) =>
      subscribe
        ? (await api.delete(`/boards/${boardId}/notifications/unsubscribe`)).data
        : (await api.post(`/boards/${boardId}/notifications/unsubscribe`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-board-notifs"] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const freqLabel = (f) => f === "daily" ? t("boardNotif.daily", "Diaria")
                        : f === "weekly" ? t("boardNotif.weekly", "Semanal")
                        : t("boardNotif.monthly", "Mensual");

  return (
    <section className="glass-panel rounded-3xl p-6" data-testid="settings-board-notifs">
      <div className="flex items-center gap-2 mb-1">
        <MailIcon className="h-5 w-5 text-blue-500" />
        <h2 className="font-display text-lg font-semibold">
          {t("settings.boardEmails", "Notificaciones de tableros")}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {t("settings.boardEmailsDesc", "Silencia los resúmenes por email de tableros específicos. Solo aplica cuando el creador los tiene activados.")}
      </p>
      {isLoading ? (
        <div className="py-6 text-sm text-muted-foreground">{t("common.loading", "Cargando…")}</div>
      ) : data.length === 0 ? (
        <div className="py-4 text-sm text-muted-foreground">
          {t("settings.boardEmailsEmpty", "Aún no perteneces a ningún tablero.")}
        </div>
      ) : (
        <ul className="space-y-2">
          {data.map(b => (
            <li key={b.board_id}
                className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/40 dark:bg-white/5 border border-white/40 dark:border-white/5"
                data-testid={`settings-board-pref-${b.board_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{b.name}</div>
                <div className="text-xs text-muted-foreground">
                  {b.digest_enabled ? (
                    <>{freqLabel(b.frequency)} {b.frequency === "daily" ? `· ${String(b.hour).padStart(2, "0")}:00` : ""}</>
                  ) : (
                    <span className="italic">{t("settings.digestOffByCreator", "Resúmenes desactivados por el creador")}</span>
                  )}
                </div>
              </div>
              <Switch
                checked={b.user_subscribed}
                disabled={!b.digest_enabled}
                onCheckedChange={(v) => toggleMut.mutate({ boardId: b.board_id, subscribe: v })}
                data-testid={`settings-board-toggle-${b.board_id}`}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
